import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity,
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Hourglass,
  Info,
  RefreshCw,
  ShieldCheck,
  XCircle,
  Zap,
} from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { cn } from '@/lib/utils/helpers'
import { showApiError } from '@/lib/utils/notifications'
import { ROUTE_PATHS } from '@/config/routes'
import {
  getCronMonitor,
  getCronMonitorFailedJob,
  getCronMonitorRuns,
} from '@/features/jobs/services/jobsService'
import type {
  CronMonitorFailedJobDetail,
  CronMonitorRunPage,
  CronMonitorResponse,
  CronMonitorTimeRange,
} from '@/features/jobs/types'
import { CronMonitorDonutChart } from '../components/CronMonitorDonutChart'
import { CronMonitorSuccessRateBanner } from '../components/CronMonitorSuccessRateBanner'
import { CRON_MONITOR_SUMMARY_STATS } from '../data/staticCronMonitorData'
import type {
  CronAttentionType,
  CronAttentionSeverity,
  CronMonitorSummaryStat,
} from '../types'

const STAT_ICON_MAP = {
  successful: CheckCircle2,
  failed: XCircle,
  running: Clock3,
  missed: Zap,
  stuck: AlertTriangle,
  waiting: Hourglass,
} as const

const STAT_TONE_CLASSES: Record<CronMonitorSummaryStat['tone'], string> = {
  success: 'bg-emerald-50 text-emerald-600',
  danger: 'bg-rose-50 text-rose-600',
  warning: 'bg-amber-50 text-amber-600',
  purple: 'bg-violet-50 text-violet-600',
}

const STAT_HREF: Record<string, (timeRange: CronMonitorTimeRange) => string> = {
  successful: (timeRange) => `${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=successful&time_range=${timeRange}`,
  failed: (timeRange) => `${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=failed&time_range=${timeRange}`,
  running: () => `${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=running`,
  missed: () => `${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=missed`,
  stuck: () => `${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=stuck`,
  waiting: () => `${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=waiting`,
}

function timestampMs(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number') return value < 10_000_000_000 ? value * 1000 : value
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

function formatDateTime(value: string | number | null | undefined): string {
  const timestamp = timestampMs(value)
  if (timestamp == null) return '—'
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDurationFrom(value: string | number | null | undefined): string {
  const timestamp = timestampMs(value)
  if (timestamp == null) return '—'
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000))
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  return `${seconds}s`
}

function formatDurationSeconds(secondsValue: number): string {
  const seconds = Math.max(0, Math.floor(secondsValue))
  if (seconds >= 86400) return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m`
  return `${seconds}s`
}

function formatTypeLabel(queue: string | null | undefined): string {
  if (!queue) return '—'
  if (queue === 'onDemand') return 'On demand'
  if (queue === 'default') return 'Default'
  if (queue === 'otp') return 'OTP'
  return queue
}

const ATTENTION_TABS: { id: CronAttentionType; label: string }[] = [
  { id: 'failures', label: 'Failures' },
  { id: 'missed', label: 'Missed' },
  { id: 'stuck', label: 'Stuck' },
  { id: 'waiting', label: 'Waiting' },
]

function missedSeverity(overdueSeconds: number): CronAttentionSeverity {
  if (overdueSeconds >= 6 * 60 * 60) return 'high'
  if (overdueSeconds >= 60 * 60) return 'medium'
  return 'low'
}

function SummaryStatCard({
  stat,
  href,
}: {
  stat: CronMonitorSummaryStat
  href: string
}) {
  const Icon = STAT_ICON_MAP[stat.id as keyof typeof STAT_ICON_MAP] ?? Activity

  return (
    <Link
      to={href}
      className="flex min-h-[110px] items-center justify-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm transition hover:border-[#1597ff]/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1597ff]/30"
    >
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          STAT_TONE_CLASSES[stat.tone],
        )}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div>
        <p className="text-sm font-medium text-slate-600">{stat.label}</p>
        <p className="mt-1 text-3xl font-bold leading-8 tracking-tight text-slate-900">{stat.value}</p>
        {stat.sublabel ? (
          <p className="mt-0.5 text-[11px] text-slate-400">{stat.sublabel}</p>
        ) : null}
      </div>
    </Link>
  )
}

export function CronMonitorPage() {
  const [attentionTab, setAttentionTab] = useState<CronAttentionType>('failures')
  const [monitor, setMonitor] = useState<CronMonitorResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<CronMonitorTimeRange>('24h')
  const [errorDetail, setErrorDetail] = useState<CronMonitorFailedJobDetail | null>(null)
  const [errorDetailLoading, setErrorDetailLoading] = useState(false)
  const [runHistoryOpen, setRunHistoryOpen] = useState(false)
  const [runHistory, setRunHistory] = useState<CronMonitorRunPage | null>(null)
  const [runHistoryLoading, setRunHistoryLoading] = useState(false)

  const loadMonitor = useCallback(async () => {
    setLoading(true)
    try {
      setMonitor(await getCronMonitor(3, timeRange))
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [timeRange])

  useEffect(() => {
    void loadMonitor()
  }, [loadMonitor])

  const summaryStats = useMemo(
    () =>
      CRON_MONITOR_SUMMARY_STATS.map((stat) => {
        if (!monitor) {
          return {
            ...stat,
            value: '—',
            sublabel: loading ? 'Loading…' : 'Unavailable',
          }
        }
        if (stat.id === 'successful' && monitor) {
          return { ...stat, value: String(monitor.summary.successful) }
        }
        if (stat.id === 'failed' && monitor) {
          return { ...stat, value: String(monitor.summary.failed), sublabel: 'Failed queue jobs' }
        }
        if (stat.id === 'running' && monitor) {
          return {
            ...stat,
            value: String(monitor.summary.running),
            sublabel: 'Reserved by a worker',
          }
        }
        if (stat.id === 'missed' && monitor) {
          return {
            ...stat,
            value: String(monitor.summary.missed),
            sublabel: 'Past expected run time',
          }
        }
        if (stat.id === 'stuck' && monitor) {
          return {
            ...stat,
            value: String(monitor.summary.stuck),
            sublabel: 'Reserved > 30 min',
          }
        }
        if (stat.id === 'waiting' && monitor) {
          return {
            ...stat,
            value: String(monitor.summary.queued),
            sublabel: 'Not yet reserved',
          }
        }
        return stat
      }),
    [loading, monitor],
  )

  const attentionItems = useMemo(() => {
    if (!monitor) return []

    const failures = monitor.failed_jobs.map((job) => ({
      id: `failure-${job.id}`,
      failedJobId: job.id,
      type: 'failures' as const,
      jobName: job.job_name,
      occurredAt: formatDateTime(job.failed_at),
      duration: job.duration_ms == null
        ? '—'
        : formatDurationSeconds(job.duration_ms / 1000),
      error: job.error_summary || 'Queue job failed',
      severity: (job.severity ?? 'medium') as CronAttentionSeverity,
    }))

    const stuck = monitor.stuck_jobs.map((job) => ({
      id: `stuck-${job.id}`,
      failedJobId: undefined,
      type: 'stuck' as const,
      jobName: job.job_name,
      occurredAt: formatDateTime(job.reserved_at),
      duration: formatDurationFrom(job.reserved_at),
      error: `Reserved for ${formatDurationFrom(job.reserved_at)} · Attempts: ${job.attempts ?? 0}`,
      severity: (job.severity ?? 'low') as CronAttentionSeverity,
    }))

    const waiting = (monitor.waiting_jobs ?? []).map((job) => {
      const queuedAt = job.available_at ?? job.created_at
      return {
        id: `waiting-${job.id}`,
        failedJobId: undefined,
        type: 'waiting' as const,
        jobName: job.job_name,
        occurredAt: formatDateTime(queuedAt),
        duration: formatDurationFrom(queuedAt),
        error: `Waiting in queue · Attempts: ${job.attempts ?? 0}`,
        severity: 'low' as CronAttentionSeverity,
      }
    })

    const missed = monitor.missed_jobs.map((job) => ({
      id: `missed-${job.schedule_key}`,
      failedJobId: undefined,
      type: 'missed' as const,
      jobName: job.job_name,
      occurredAt: formatDateTime(job.expected_at),
      duration: formatDurationSeconds(job.overdue_seconds),
      error: job.last_run_at
        ? `Last run: ${formatDateTime(job.last_run_at)}`
        : 'No recorded run since monitoring started',
      severity: (job.severity ?? missedSeverity(job.overdue_seconds)) as CronAttentionSeverity,
    }))

    return [
      ...failures,
      ...missed,
      ...stuck,
      ...waiting,
    ]
  }, [monitor])

  const attentionCounts = useMemo(
    () => ({
      failures: monitor?.summary.failed
        ?? attentionItems.filter((item) => item.type === 'failures').length,
      missed: monitor?.summary.missed
        ?? attentionItems.filter((item) => item.type === 'missed').length,
      stuck: monitor?.summary.stuck
        ?? attentionItems.filter((item) => item.type === 'stuck').length,
      waiting: monitor?.summary.queued
        ?? attentionItems.filter((item) => item.type === 'waiting').length,
    }),
    [attentionItems, monitor],
  )

  const attentionChart = useMemo(() => {
    const values = {
      failed: attentionCounts.failures,
      stuck: attentionCounts.stuck,
      missed: attentionCounts.missed,
      waiting: attentionCounts.waiting,
    }
    const total = values.failed + values.stuck + values.missed + values.waiting
    const percent = (value: number) => (
      total > 0 ? Number(((value / total) * 100).toFixed(1)) : 0
    )

    return {
      total,
      segments: [
        {
          label: 'Failed',
          value: values.failed,
          percent: percent(values.failed),
          color: '#ef4444',
        },
        {
          label: 'Stuck',
          value: values.stuck,
          percent: percent(values.stuck),
          color: '#3b82f6',
        },
        {
          label: 'Missed',
          value: values.missed,
          percent: percent(values.missed),
          color: '#a855f7',
        },
        {
          label: 'Waiting',
          value: values.waiting,
          percent: percent(values.waiting),
          color: '#eab308',
        },
      ],
    }
  }, [attentionCounts])

  const successfulJobs = useMemo(() => {
    if (!monitor?.successful_jobs) return []
    return monitor.successful_jobs.map((job) => ({
      id: job.id,
      jobName: job.job_name,
      completedAt: formatDateTime(job.completed_at ?? job.started_at),
      duration: job.duration_ms == null
        ? '—'
        : formatDurationSeconds(job.duration_ms / 1000),
      type: formatTypeLabel(job.queue),
    }))
  }, [monitor])

  const filteredAttention = useMemo(
    () => attentionItems.filter((item) => item.type === attentionTab),
    [attentionItems, attentionTab],
  )

  const lastUpdated = monitor ? formatDateTime(monitor.generated_at) : 'Not loaded'

  const openFullError = async (failedJobId: number) => {
    setErrorDetailLoading(true)
    try {
      setErrorDetail(await getCronMonitorFailedJob(failedJobId))
    } catch (error) {
      showApiError(error)
    } finally {
      setErrorDetailLoading(false)
    }
  }

  const loadRunHistory = async (page = 1) => {
    setRunHistoryLoading(true)
    try {
      setRunHistory(await getCronMonitorRuns(page))
    } catch (error) {
      showApiError(error)
    } finally {
      setRunHistoryLoading(false)
    }
  }

  const openRunHistory = () => {
    setRunHistoryOpen(true)
    void loadRunHistory(1)
  }

  return (
    <PageShell
      title="CRON MONITORING"
      pageHeaderTestIdRoot="cron-monitor-page-header"
      contentClassName="px-3 py-4 sm:px-5 sm:py-5 space-y-4"
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold leading-8 text-slate-900">Cron Monitoring</h2>
          <p className="mt-1 text-sm text-slate-500">
            Monitor scheduled jobs, failures, and run health
          </p>
        </div>

        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-start lg:w-auto lg:justify-end">
          <label className="relative block w-full sm:w-[165px]">
            <span className="sr-only">Monitor time range</span>
            <CalendarDays className="pointer-events-none absolute left-3 top-[13px] h-4 w-4 text-slate-500" />
            <select
              value={timeRange}
              onChange={(event) => setTimeRange(event.target.value as CronMonitorTimeRange)}
              disabled={loading}
              className="h-11 w-full appearance-none rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-9 text-sm font-semibold text-slate-700 shadow-sm focus:border-[#1597ff] focus:outline-none focus:ring-2 focus:ring-[#1597ff]/20 disabled:cursor-wait disabled:opacity-60"
            >
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="all">All time</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-[13px] h-4 w-4 text-slate-400" />
          </label>

          <div className="flex flex-col items-stretch gap-0.5">
          <button
            type="button"
            onClick={() => void loadMonitor()}
            disabled={loading}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-[140px]"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} aria-hidden />
            Refresh
          </button>
            <span className="text-center text-[11px] text-slate-400">
              Last updated: {lastUpdated}
            </span>
          </div>
        </div>
      </div>

      <CronMonitorSuccessRateBanner
        successful={monitor?.runs_last_24_hours?.successful ?? null}
        total={monitor?.runs_last_24_hours?.total ?? null}
        percent={monitor?.runs_last_24_hours?.success_rate ?? null}
        loading={loading}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
        <Link
          to={`${ROUTE_PATHS.DASHBOARD_JOBS_CRON}?cron_status=ACTIVE`}
          className="flex min-h-[110px] items-center justify-center gap-4 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50/80 to-white px-5 py-4 shadow-sm transition hover:border-emerald-400 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 sm:col-span-2 xl:col-span-1"
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
            <ShieldCheck className="h-6 w-6" aria-hidden />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-600">Scheduled Jobs</p>
            <p className="mt-1 text-3xl font-bold leading-8 tracking-tight text-emerald-700">
              {monitor?.summary.scheduled ?? 0}
            </p>
            <p className="mt-0.5 text-[11px] text-slate-400">View all cron jobs</p>
          </div>
        </Link>

        {summaryStats.map((stat) => (
          <SummaryStatCard
            key={stat.id}
            stat={stat}
            href={(STAT_HREF[stat.id] ?? (() => ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR))(timeRange)}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 items-stretch gap-3 xl:grid-cols-[1.72fr_1fr]">
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="px-4 pt-4">
            <h3 className="text-base font-semibold text-slate-900">Needs attention</h3>
            <div className="mt-3 flex flex-wrap gap-2">
                {ATTENTION_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setAttentionTab(tab.id)}
                    className={cn(
                      'inline-flex h-8 items-center gap-2 rounded-md border px-3.5 text-xs font-semibold transition-colors',
                      attentionTab === tab.id
                        ? tab.id === 'failures'
                          ? 'border-rose-300 bg-rose-50 text-slate-700'
                          : 'border-slate-300 bg-slate-100 text-slate-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                    )}
                  >
                    {tab.label}
                    <span
                      className={cn(
                        'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px]',
                        tab.id === 'failures'
                            ? 'bg-rose-100 text-rose-700'
                            : tab.id === 'missed'
                              ? 'bg-amber-100 text-amber-700'
                              : tab.id === 'waiting'
                                ? 'bg-sky-100 text-sky-700'
                                : 'bg-slate-100 text-slate-600',
                      )}
                    >
                      {attentionCounts[tab.id]}
                    </span>
                  </button>
                ))}
            </div>
          </div>

          <div className="mt-3 overflow-x-auto px-3">
            <table className="w-full min-w-[680px] border-separate border-spacing-0 overflow-hidden rounded-md border border-slate-200 text-left text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-3 py-3 font-semibold">Job</th>
                  <th className="px-3 py-3 font-semibold">Last occurred</th>
                  <th className="px-3 py-3 font-semibold">Error</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttention.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-8 text-center text-xs text-slate-500">
                      No {attentionTab} found.
                    </td>
                  </tr>
                ) : filteredAttention.map((item) => (
                  <tr key={item.id} className="border-t border-slate-100 [&+tr_td]:border-t [&+tr_td]:border-slate-100">
                    <td className="px-3 py-3 font-semibold text-slate-800">
                      <span className="mr-2 inline-block h-2 w-2 rounded-full bg-rose-500" />
                      {item.jobName}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-slate-700">
                      <div>{item.occurredAt}</div>
                      <div className="text-[11px] text-slate-500">Duration: {item.duration}</div>
                    </td>
                    <td className="max-w-[280px] px-3 py-3 text-slate-700">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate">{item.error}</span>
                        {item.failedJobId ? (
                          <button
                            type="button"
                            onClick={() => void openFullError(item.failedJobId!)}
                            disabled={errorDetailLoading}
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-[#1597ff] transition hover:bg-sky-100 disabled:cursor-wait disabled:opacity-50"
                            aria-label={`View full error for ${item.jobName}`}
                            title="View full error"
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3.5 text-center">
            <Link
              to={
                attentionTab === 'missed'
                  ? `${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=missed`
                  : attentionTab === 'stuck'
                    ? `${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=stuck`
                    : attentionTab === 'waiting'
                      ? `${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=waiting`
                      : `${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=failed&time_range=${timeRange}`
              }
              className="text-xs font-semibold text-[#1597ff] hover:underline"
            >
              {attentionTab === 'missed'
                ? 'View all missed'
                : attentionTab === 'stuck'
                  ? 'View all stuck'
                  : attentionTab === 'waiting'
                    ? 'View all waiting'
                    : 'View all failures'}
            </Link>
          </div>
        </section>

        <section className="flex min-h-[360px] flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:min-h-full">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-slate-900">Attention breakdown</h3>
            <button
              type="button"
              onClick={openRunHistory}
              className="text-xs font-semibold text-[#1597ff] hover:underline"
            >
              View all runs
            </button>
          </div>
          <div className="mt-3 flex flex-1 items-center">
            <CronMonitorDonutChart
              segments={attentionChart.segments}
              totalLabel={String(attentionChart.total)}
              centerCaption="Total issues"
            />
          </div>
        </section>
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3.5">
          <h3 className="text-base font-semibold text-slate-900">Successful jobs</h3>
          <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
            {monitor?.summary.successful ?? successfulJobs.length}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-xs">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Job</th>
                <th className="px-4 py-3 font-semibold">Completed</th>
                <th className="px-4 py-3 font-semibold">Duration</th>
                <th className="px-4 py-3 font-semibold">Type</th>
              </tr>
            </thead>
            <tbody>
              {successfulJobs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                    No successful jobs in this range.
                  </td>
                </tr>
              ) : (
                successfulJobs.map((job) => (
                  <tr key={job.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-semibold text-slate-800">
                      <span className="mr-2 inline-block h-2 w-2 rounded-full bg-emerald-500" />
                      {job.jobName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">{job.completedAt}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">{job.duration}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold',
                          job.type === 'On demand'
                            ? 'bg-sky-100 text-sky-700'
                            : 'bg-slate-100 text-slate-700',
                        )}
                      >
                        {job.type}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3.5 text-center">
          <Link
            to={`${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?kind=successful&time_range=${timeRange}`}
            className="text-xs font-semibold text-[#1597ff] hover:underline"
          >
            View all successful
          </Link>
        </div>
      </section>

      <OnScreenModal
        isOpen={errorDetail !== null}
        title="Failed Job Error"
        onClose={() => setErrorDetail(null)}
        maxWidth="max-w-900"
        bodyScrollOnly
        bodyClassName="max-h-[70vh]"
      >
        {errorDetail ? (
          <div className="space-y-4">
            <div className="grid gap-3 rounded-lg bg-slate-50 p-4 text-sm sm:grid-cols-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Job</p>
                <p className="mt-1 font-semibold text-slate-900">{errorDetail.job_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Queue</p>
                <p className="mt-1 text-slate-800">{errorDetail.queue}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Failed at</p>
                <p className="mt-1 text-slate-800">{formatDateTime(errorDetail.failed_at)}</p>
              </div>
            </div>
            <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-rose-200 bg-rose-50 p-4 font-mono text-xs leading-5 text-rose-900">
              {errorDetail.exception}
            </pre>
          </div>
        ) : null}
      </OnScreenModal>

      <OnScreenModal
        isOpen={runHistoryOpen}
        title="Cron Runs — Last 24 Hours"
        onClose={() => setRunHistoryOpen(false)}
        maxWidth="max-w-900"
        bodyScrollOnly
        bodyClassName="max-h-[70vh]"
      >
        {runHistoryLoading && !runHistory ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading run history…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full min-w-[760px] text-left text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-3 py-2.5 font-semibold">Job</th>
                    <th className="px-3 py-2.5 font-semibold">Started</th>
                    <th className="px-3 py-2.5 font-semibold">Duration</th>
                    <th className="px-3 py-2.5 font-semibold">Attempt</th>
                    <th className="px-3 py-2.5 font-semibold">Source</th>
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {runHistory?.data.length ? (
                    runHistory.data.map((run) => (
                      <tr key={run.id} className="border-t border-slate-100">
                        <td className="px-3 py-2.5 font-semibold text-slate-800">
                          {run.job_name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">
                          {formatDateTime(run.started_at)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-slate-600">
                          {run.duration_ms == null
                            ? '—'
                            : formatDurationSeconds(run.duration_ms / 1000)}
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">{run.attempt}</td>
                        <td className="px-3 py-2.5 text-slate-600">{run.source}</td>
                        <td className="px-3 py-2.5">
                          <span className={cn(
                            'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                            run.status === 'COMPLETED'
                              ? 'bg-emerald-100 text-emerald-700'
                              : run.status === 'FAILED'
                                ? 'bg-rose-100 text-rose-700'
                                : run.status === 'RUNNING'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-sky-100 text-sky-700',
                          )}>
                            {run.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                        No monitored cron runs in the last 24 hours.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {runHistory ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-500">
                  Showing {runHistory.from ?? 0}–{runHistory.to ?? 0} of {runHistory.total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void loadRunHistory(runHistory.current_page - 1)}
                    disabled={runHistoryLoading || runHistory.current_page <= 1}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-slate-500">
                    Page {runHistory.current_page} of {runHistory.last_page}
                  </span>
                  <button
                    type="button"
                    onClick={() => void loadRunHistory(runHistory.current_page + 1)}
                    disabled={runHistoryLoading || runHistory.current_page >= runHistory.last_page}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </OnScreenModal>
    </PageShell>
  )
}
